/**
 * Leads (manuales + self-service unificados). Antes esta pestaña no tenía
 * filtros, búsqueda, KPIs ni refresh — a diferencia de Onboarding, que sí.
 * Ahora muestra también el paso del wizard cuando el lead viene de self-service.
 */
import { useCallback, useEffect, useState } from "react";
import { Loader2, RefreshCw, MessageCircle, Mail } from "lucide-react";
import { toast } from "sonner";
import { apiGetJson, apiSendJson } from "@/lib/sm-api";
import { CrudEmptyState } from "@/components/ds";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { UserPlus } from "lucide-react";
import { buildLeadWhatsAppUrl } from "@/lib/lead-whatsapp";
import { ONBOARDING_STATUS_CONFIG } from "./OnboardingSessionsPanel";
import type { OnboardingStatus } from "@/lib/self-service-provisioning";

export type LeadRow = {
  id: string;
  nombre: string;
  negocio: string;
  email: string;
  telefono: string;
  plan_interesado: string;
  status: string;
  created_at: string;
  form_data?: Record<string, unknown> | null;
  onboarding_status?: OnboardingStatus | null;
  onboarding_session_id?: string | null;
};

const STATUS_LABELS: Record<string, string> = {
  pending: "Pendiente",
  approved: "Aprobado",
  rejected: "Rechazado",
  converted: "Convertido",
};

function isSameDay(a: Date, b: Date) {
  return a.toDateString() === b.toDateString();
}

const PAGE_SIZE = 200;

export function LeadsPanel({ onViewDetail }: { onViewDetail: (leadId: string) => void }) {
  const [leads, setLeads] = useState<LeadRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [acting, setActing] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [originFilter, setOriginFilter] = useState<string>("all");
  const [planFilter, setPlanFilter] = useState("");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [q, setQ] = useState("");

  const buildParams = useCallback(
    (offset: number) => {
      const params = new URLSearchParams();
      if (statusFilter !== "all") params.set("status", statusFilter);
      if (planFilter.trim()) params.set("plan", planFilter.trim());
      if (fromDate) params.set("from", fromDate);
      if (toDate) params.set("to", toDate);
      if (q.trim()) params.set("q", q.trim());
      params.set("limit", String(PAGE_SIZE));
      params.set("offset", String(offset));
      return params;
    },
    [statusFilter, planFilter, fromDate, toDate, q],
  );

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await apiGetJson<LeadRow[]>(`/api/leads?${buildParams(0)}`);
      setLeads(data);
      setHasMore(data.length === PAGE_SIZE);
    } catch {
      toast.error("Error al cargar leads");
    } finally {
      setLoading(false);
    }
  }, [buildParams]);

  const loadMore = async () => {
    setLoadingMore(true);
    try {
      const data = await apiGetJson<LeadRow[]>(`/api/leads?${buildParams(leads.length)}`);
      setLeads((prev) => [...prev, ...data]);
      setHasMore(data.length === PAGE_SIZE);
    } catch {
      toast.error("Error al cargar más leads");
    } finally {
      setLoadingMore(false);
    }
  };

  useEffect(() => { void load(); }, [load]);

  const approveLead = async (lead: LeadRow) => {
    setActing(lead.id + "approve");
    try {
      const res = await apiSendJson<{ slug: string }>("/api/leads", "PATCH", {
        leadId: lead.id, action: "approve", trialDays: 14,
      });
      toast.success(`Workspace creado: /market/${res.slug}`);
      void load();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Error al aprobar");
    } finally {
      setActing(null);
    }
  };

  const rejectLead = async (leadId: string) => {
    setActing(leadId + "reject");
    try {
      await apiSendJson("/api/leads", "PATCH", { leadId, action: "reject", rejectReason: "No cumple criterios actuales" });
      toast.success("Lead rechazado");
      void load();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Error al rechazar");
    } finally {
      setActing(null);
    }
  };

  const filtered = leads.filter((l) => {
    if (originFilter === "manual" && l.form_data?.source === "self_service_checkout") return false;
    if (originFilter === "self_service" && l.form_data?.source !== "self_service_checkout") return false;
    return true;
  });

  const today = new Date();
  const todayCount = leads.filter((l) => isSameDay(new Date(l.created_at), today)).length;
  const weekAgo = new Date(today.getTime() - 7 * 24 * 60 * 60 * 1000);
  const weekCount = leads.filter((l) => new Date(l.created_at) >= weekAgo).length;
  const pendingCount = leads.filter((l) => l.status === "pending").length;
  const convertedCount = leads.filter((l) => l.status === "approved" || l.status === "converted").length;
  const conversionRate = leads.length ? Math.round((convertedCount / leads.length) * 100) : 0;

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <Card><CardContent className="p-3">
          <p className="text-xs text-muted-foreground">Nuevos hoy</p>
          <p className="text-xl font-bold">{todayCount}</p>
        </CardContent></Card>
        <Card><CardContent className="p-3">
          <p className="text-xs text-muted-foreground">Nuevos 7 días</p>
          <p className="text-xl font-bold">{weekCount}</p>
        </CardContent></Card>
        <Card><CardContent className="p-3">
          <p className="text-xs text-muted-foreground">Pendientes</p>
          <p className="text-xl font-bold">{pendingCount}</p>
        </CardContent></Card>
        <Card><CardContent className="p-3">
          <p className="text-xs text-muted-foreground">Tasa de conversión</p>
          <p className="text-xl font-bold">{conversionRate}%</p>
        </CardContent></Card>
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <Input
          placeholder="Buscar por email, nombre o negocio…"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          className="w-64"
        />
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-40"><SelectValue placeholder="Estado" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todos los estados</SelectItem>
            <SelectItem value="pending">Pendiente</SelectItem>
            <SelectItem value="approved">Aprobado</SelectItem>
            <SelectItem value="rejected">Rechazado</SelectItem>
          </SelectContent>
        </Select>
        <Select value={originFilter} onValueChange={setOriginFilter}>
          <SelectTrigger className="w-40"><SelectValue placeholder="Origen" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todos los orígenes</SelectItem>
            <SelectItem value="manual">Manual</SelectItem>
            <SelectItem value="self_service">Self-service</SelectItem>
          </SelectContent>
        </Select>
        <Input
          placeholder="Plan de interés"
          value={planFilter}
          onChange={(e) => setPlanFilter(e.target.value)}
          className="w-36"
        />
        <Input
          type="date"
          value={fromDate}
          onChange={(e) => setFromDate(e.target.value)}
          className="w-36"
          aria-label="Desde"
        />
        <Input
          type="date"
          value={toDate}
          onChange={(e) => setToDate(e.target.value)}
          className="w-36"
          aria-label="Hasta"
        />
        <Button variant="outline" size="sm" onClick={() => void load()} disabled={loading}>
          <RefreshCw className={`mr-1.5 h-4 w-4 ${loading ? "animate-spin" : ""}`} />
          Actualizar
        </Button>
        <span className="ml-auto text-xs text-muted-foreground">{filtered.length} de {leads.length} cargados</span>
      </div>

      {loading && leads.length === 0 ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
        </div>
      ) : filtered.length === 0 ? (
        <CrudEmptyState icon={UserPlus} title="Sin leads" description="Los leads manuales y de self-service aparecerán acá." />
      ) : (
        <div className="space-y-2">
          {filtered.map((l) => {
            const isSelfService = l.form_data?.source === "self_service_checkout";
            const stepCfg = l.onboarding_status ? ONBOARDING_STATUS_CONFIG[l.onboarding_status] : null;
            return (
              <Card key={l.id}>
                <CardHeader className="pb-2">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="font-medium text-sm">{l.negocio}</span>
                    <Badge variant={l.status === "pending" ? "secondary" : l.status === "approved" ? "default" : "outline"}>
                      {STATUS_LABELS[l.status] ?? l.status}
                    </Badge>
                    {isSelfService ? <Badge variant="outline" className="text-[10px]">self-service</Badge> : null}
                    {stepCfg ? (
                      <Badge className={`text-[10px] border-0 ${stepCfg.color}`}>{stepCfg.label}</Badge>
                    ) : null}
                    <span className="ml-auto text-xs text-muted-foreground">
                      {new Date(l.created_at).toLocaleDateString("es-AR", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit", hour12: false })}
                    </span>
                  </div>
                </CardHeader>
                <CardContent className="pt-0">
                  <p className="text-xs text-muted-foreground">
                    {l.nombre} · {l.email}{l.telefono && l.telefono !== "—" ? ` · ${l.telefono}` : ""} · {l.plan_interesado}
                  </p>
                  <div className="mt-2 flex flex-wrap gap-2">
                    <Button size="sm" variant="outline" onClick={() => onViewDetail(l.id)}>Ver detalle</Button>
                    {l.status === "pending" && (
                      <>
                        <Button size="sm" disabled={acting === l.id + "approve"} onClick={() => void approveLead(l)}>
                          {acting === l.id + "approve" ? <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" /> : null}
                          Aprobar trial
                        </Button>
                        <Button size="sm" variant="ghost" disabled={acting === l.id + "reject"} onClick={() => void rejectLead(l.id)}>
                          Rechazar
                        </Button>
                      </>
                    )}
                    {l.telefono && l.telefono !== "—" ? (
                      <Button size="sm" variant="outline" className="gap-1.5" asChild>
                        <a href={buildLeadWhatsAppUrl({ nombre: l.nombre, negocio: l.negocio, email: l.email, telefono: l.telefono, planInteresado: l.plan_interesado })} target="_blank" rel="noreferrer">
                          <MessageCircle className="h-3.5 w-3.5" /> WhatsApp
                        </a>
                      </Button>
                    ) : null}
                    <Button size="sm" variant="outline" className="gap-1.5" asChild>
                      <a href={`mailto:${l.email}?subject=${encodeURIComponent(`Tu solicitud en Structura Market — ${l.negocio}`)}`}>
                        <Mail className="h-3.5 w-3.5" /> Email
                      </a>
                    </Button>
                  </div>
                </CardContent>
              </Card>
            );
          })}
          {hasMore && (
            <div className="flex justify-center pt-2">
              <Button variant="outline" size="sm" onClick={() => void loadMore()} disabled={loadingMore}>
                {loadingMore ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : null}
                Cargar más
              </Button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
